import React, { useMemo } from 'react';
import type { InterviewRow } from '../../data/clientEngagementMock';
import { formatNlAgendaDayHeading } from './engagementLabels';
import UpcomingInterviewCard, { type PortalInterviewFollowUpKind } from './UpcomingInterviewCard';

export type { PortalInterviewFollowUpKind };

export interface InterviewAgendaProps {
  interviews: InterviewRow[];
  noteTextById: Record<string, string>;
  onAutosaveNote: (id: string, text: string) => void;
  onSendHeadsUp: (id: string, text: string) => void;
  onPortalFollowUp: (inv: InterviewRow, kind: PortalInterviewFollowUpKind, comment: string) => void | Promise<void>;
}

const InterviewAgenda: React.FC<InterviewAgendaProps> = ({
  interviews,
  noteTextById,
  onAutosaveNote,
  onSendHeadsUp,
  onPortalFollowUp,
}) => {
  const groups = useMemo(() => {
    const m = new Map<string, InterviewRow[]>();
    const sorted = [...interviews].sort((a, b) => new Date(a.startsAt).getTime() - new Date(b.startsAt).getTime());
    for (const inv of sorted) {
      let key: string;
      try {
        key = new Date(inv.startsAt).toISOString().slice(0, 10);
      } catch {
        key = '—';
      }
      if (!m.has(key)) m.set(key, []);
      m.get(key)!.push(inv);
    }
    return Array.from(m.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [interviews]);

  if (!interviews.length) {
    return (
      <p className="rounded-2xl border border-dashed border-slate-200 bg-slate-50/80 px-4 py-10 text-center text-sm text-slate-500">
        Geen geplande gesprekken in dit venster. Zodra je recruiter interviews inplant, verschijnen ze hier op datum.
      </p>
    );
  }

  return (
    <div className="space-y-10">
      {groups.map(([dayKey, rows]) => (
        <section key={dayKey} className="scroll-mt-6">
          <h3 className="mb-4 border-b border-slate-100 pb-2 text-sm font-bold capitalize tracking-tight text-slate-800">
            {dayKey === '—' ? 'Onbekende datum' : formatNlAgendaDayHeading(dayKey)}
          </h3>
          <div className="grid gap-4 md:grid-cols-2">
            {rows.map((inv) => (
              <UpcomingInterviewCard
                key={inv.id}
                interview={inv}
                noteText={noteTextById[inv.id] || ''}
                onAutosaveNote={onAutosaveNote}
                onSendHeadsUp={onSendHeadsUp}
                onPortalFollowUp={onPortalFollowUp}
              />
            ))}
          </div>
        </section>
      ))}
    </div>
  );
};

export default InterviewAgenda;
